import { EmptyState } from "@pessoas/ui";
import { t } from "./i18n.js";

/**
 * O envelope de toda página.
 *
 * O eyebrow em caixa alta acima do título é a assinatura tipográfica da casa
 * (`.md-eyebrow` no MKT). Ele resolve um problema real: dizer ONDE a pessoa
 * está sem gastar uma linha de breadcrumb.
 */
export function Pagina(
  { titulo, secao, descricao, acoes, carimbo, children }:
  {
    // `| undefined` explícito: com `exactOptionalPropertyTypes`, "ausente" e
    // "presente e undefined" são coisas diferentes, e todo chamador que passa
    // um valor condicional cai no segundo caso.
    titulo: string;
    secao?: string | undefined;
    descricao?: string | undefined;
    acoes?: React.ReactNode | undefined;
    carimbo?: string | undefined;
    children: React.ReactNode;
  },
) {
  return (
    <section className="mx-auto w-full max-w-[1440px] space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4 surge">
        <div className="min-w-0">
          {secao && <p className="eyebrow">{secao}</p>}
          <h1 className="titulo-pagina mt-1.5">{titulo}</h1>
          {descricao && (
            <p className="mt-2 max-w-2xl text-sm leading-relaxed"
               style={{ color: "var(--muted-foreground)" }}>
              {descricao}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {carimbo && <span className="carimbo">{carimbo}</span>}
          {acoes}
        </div>
      </header>
      {children}
    </section>
  );
}

/**
 * Variação entre dois períodos, em ponto percentual do próprio valor.
 *
 * De zero para qualquer coisa NÃO é "aumento de infinito por cento": devolve
 * `null`, e a tela mostra o número absoluto. Percentual sobre base zero é a
 * conta que enche painel de "+∞%" e faz o usuário parar de ler o cartão.
 */
export function variacao(atual: number, anterior: number): number | null {
  if (anterior === 0) return null;
  return ((atual - anterior) / Math.abs(anterior)) * 100;
}

/**
 * Cartão de número.
 *
 * Três camadas, nesta ordem de leitura: o QUE é (rótulo), QUANTO é (número em
 * mono tabular) e COMPARADO A QUÊ (delta mais o período).
 *
 * O período é obrigatório junto do delta. "+12%" sozinho é ruído: o leitor não
 * sabe se é contra o mês passado, contra a meta ou contra o ano. É o defeito
 * mais comum em painel de SaaS, e o que faz um cartão parecer enfeite.
 */
export function Indicador(
  { rotulo, valor, sinal, detalhe, atraso, delta, periodo, serie, inverso }:
  {
    rotulo: string; valor: string;
    sinal?: "bom" | "atencao" | "critico";
    detalhe?: string; atraso?: 1 | 2 | 3 | 4;
    /** Variação percentual já calculada. `null` quando a base era zero. */
    delta?: number | null;
    /** Contra o quê. Sem isto, o delta não é mostrado. */
    periodo?: string;
    /** Série para a miniatura. Menos de 3 pontos não vira linha. */
    serie?: readonly number[];
    /** Para métrica em que SUBIR é ruim: turnover, absenteísmo, rejeição. */
    inverso?: boolean;
  },
) {
  // `delta === null` significa base zero, e aí percentual não existe: "de 0
  // para 1" não é "+100%", é uma comparação absoluta. O período continua
  // valendo, e é ele que dá sentido ao número.
  const temDelta = delta !== undefined && delta !== null && periodo !== undefined;
  const baseZero = delta === null && periodo !== undefined;
  const subiu = temDelta && delta > 0;
  // Em turnover, subir é o problema. Amarrar a cor ao sinal aritmético faria o
  // cartão pintar de verde a rotatividade crescendo.
  const bom = temDelta && (inverso ? delta < 0 : delta > 0);
  const neutro = temDelta && Math.abs(delta) < 0.5;

  return (
    <div className={`painel painel-interativo indicador p-4 pl-5 surge ${atraso ? `surge-${atraso}` : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {sinal && <span aria-hidden className={`sinal sinal-${sinal}`} />}
          <span className="eyebrow truncate">{rotulo}</span>
        </div>
        {temDelta && (
          <span className="eop-delta" data-tom={neutro ? "neutro" : bom ? "bom" : "ruim"}>
            <span aria-hidden>{neutro ? "→" : subiu ? "↑" : "↓"}</span>
            {/* A seta é a única coisa que diz a DIREÇÃO, e ela é aria-hidden.
                Sem este rótulo, quem usa leitor de tela ouve "12,4%" e não
                sabe se subiu ou caiu. O sinal fica fora do número justamente
                porque a seta já o carrega para quem enxerga. */}
            <span className="sr-only">
              {neutro ? "estável, variação de " : subiu ? "aumento de " : "queda de "}
            </span>
            {Math.abs(delta).toFixed(Math.abs(delta) < 10 ? 1 : 0)}%
          </span>
        )}
      </div>

      <p className="num mt-2.5 text-[1.75rem] font-semibold leading-none tracking-tight">
        {valor}
      </p>

      {serie && serie.length >= 3 && <Miniatura serie={serie} />}

      {(detalhe || temDelta || baseZero) && (
        <p className="mt-2 text-xs leading-snug" style={{ color: "var(--faint-foreground)" }}>
          {/* Com base zero mostra o PERÍODO, não o `detalhe`: o detalhe é o
              texto de "não tem nada", e ele contradizia o número logo acima
              quando o valor era 1 e o mês anterior era 0. */}
          {temDelta || baseZero ? periodo : detalhe}
        </p>
      )}
    </div>
  );
}

/**
 * Linha de tendência de 28px. Sem eixo, sem grade, sem número: ela responde
 * "está subindo ou descendo", e quem quer o valor exato clica no cartão.
 */
export function Miniatura({ serie }: { serie: readonly number[] }) {
  const max = Math.max(...serie), min = Math.min(...serie);
  const amplitude = max - min || 1;
  const passo = 100 / (serie.length - 1);
  const y = (v: number) => 26 - ((v - min) / amplitude) * 22;
  const linha = serie.map((v, i) => `${i * passo},${y(v)}`).join(" ");

  return (
    <svg aria-hidden className="eop-mini mt-3" viewBox="0 0 100 28" preserveAspectRatio="none">
      {/* A área embaixo da linha dá volume sem gastar mais um elemento. */}
      <polygon points={`0,28 ${linha} 100,28`} className="eop-mini-area" />
      <polyline points={linha} className="eop-mini-linha" />
      <circle cx={100} cy={y(serie[serie.length - 1]!)} r={2} className="eop-mini-ponta" />
    </svg>
  );
}

/** Card com cabeçalho separado por hairline, no padrão do MKT. */
export function Cartao(
  { titulo, subtitulo, acoes, children, className }:
  {
    titulo: string;
    subtitulo?: string | undefined;
    acoes?: React.ReactNode | undefined;
    children: React.ReactNode;
    className?: string | undefined;
  },
) {
  return (
    <section className={`painel ${className ?? ""}`}>
      <header className="painel-topo flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[0.9375rem] font-semibold leading-tight tracking-tight">
            {titulo}
          </h2>
          {subtitulo && (
            <p className="mt-1 text-xs" style={{ color: "var(--faint-foreground)" }}>
              {subtitulo}
            </p>
          )}
        </div>
        {acoes}
      </header>
      <div className="painel-corpo">{children}</div>
    </section>
  );
}

/**
 * `nu` para quando o esqueleto já está DENTRO de um cartão: sem isso vira
 * card dentro de card, e a tela ganha uma moldura que não significa nada.
 */
export function Carregando({ rotulo = t("estado.carregando"), linhas = 3, nu = false }:
  { rotulo?: string; linhas?: number; nu?: boolean }) {
  return (
    <div className={nu ? "space-y-2.5" : "painel painel-corpo space-y-2.5"}
         role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">{rotulo}</span>
      {/* Esqueleto por seção, não spinner de tela cheia: a tela zerada some e
          o usuário enxerga a forma do que vem. */}
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className="eop-esqueleto" style={{ width: `${100 - i * 14}%` }} />
      ))}
    </div>
  );
}

export function Falha({ erro, tentarDeNovo }: { erro: unknown; tentarDeNovo?: () => void }) {
  const mensagem = erro instanceof Error ? erro.message : String(erro);
  return (
    <EmptyState
      titulo={t("estado.erroTitulo")}
      descricao={`${t("estado.erroAjuda")} ${mensagem}`}
      tom="atencao"
      {...(tentarDeNovo
        ? { acao: <button className="botao" onClick={tentarDeNovo}>Tentar de novo</button> }
        : {})}
    />
  );
}
